import { computed, defineComponent, nextTick, PropType, ref } from "vue";
import StepBox from "../../components/StepBox";
import InputText from "../../forms/InputText";
import InputCheckbox from "../../forms/InputCheckbox";
import InputSelect from "../../forms/InputSelect";
import FormInfo from "../../components/FormInfo";
import { SmeOrderJson } from "../../utils/SmeUtils";
import Utils from "../../utils/Utils";
import Validator, { ValidationError } from "../../utils/Validator";
import LabeledInput from "../../components/LabeledInput";
import * as moment from "moment";
import Tracking from "../../utils/Tracking";

export default defineComponent({
    components: {
        InputText,
        InputCheckbox,
        InputSelect,
        StepBox,
        FormInfo,
        LabeledInput,
    },

    template: `
        <StepBox class="StepSmeFormInvoice"
            :step="2"
            :currentstep="step"
            title="Dane do umowy"

            sticky="auto"

            :valid="true"
            :back="true"
            :error="null"

            @back="$emit('goto', 1)"
            @edit="$emit('goto', 2)"
            @next="onNext"
        >
            <div class="StepSmeFormInvoice__form">
                <h3>Punkt poboru energii</h3>

                <FormInfo type="2" title="Numer PPE" :value="options.ppe" />
                <FormInfo type="2" title="Operator sieci (OSD)" :value="osd?.label" :tip="true" @tip="onTip('osd')" />
                <FormInfo type="2" title="Dotychczasowy sprzedawca" :value="prev_vendor?.label" :tip="true" @tip="onTip('vendor')" />

                <h3>Adres Punktu Poboru Energii</h3>
                <InputCheckbox title="Taki sam jak adres firmy" :modelValue="!form.ppe_active" @update:modelValue="form.ppe_active = !$event" />

                <template v-if="form.ppe_active">
                    <InputText label="Ulica" v-model="form.ppe_street" :required="true" :error="errors.ppe_street" />

                    <div class="StepForm__group">
                        <InputText label="Numer budynku" v-model="form.ppe_buildingno" :required="true" :error="errors.ppe_buildingno" />
                        <InputText label="Numer mieszkania (opcjonalnie)" v-model="form.ppe_apartmentno" :error="errors.ppe_apartmentno" />
                    </div>

                    <div class="StepForm__group23">
                        <InputText label="Kod pocztowy" v-model="form.ppe_zipcode" :required="true" mask="00-000" :error="errors.ppe_zipcode" />
                        <InputText label="Miejscowość" v-model="form.ppe_city" :required="true" :error="errors.ppe_city" />
                    </div>
                </template>

                <hr class="mt-16 mb-20">

                <h3>Adres korespondencyjny</h3>

                <InputCheckbox title="Taki sam jak adres firmy" :modelValue="!form.other_active" @update:modelValue="form.other_active = !$event" />

                <template v-if="form.other_active">
                    <InputText label="Ulica" v-model="form.other_street" :required="true" :error="errors.other_street" />

                    <div class="StepForm__group">
                        <InputText label="Numer budynku" v-model="form.other_buildingno" :required="true" :error="errors.other_buildingno" />
                        <InputText label="Numer mieszkania (opcjonalnie)" v-model="form.other_apartmentno" :error="errors.other_apartmentno" />
                    </div>

                    <div class="StepForm__group23">
                        <InputText label="Kod pocztowy" v-model="form.other_zipcode" :required="true" mask="00-000" :error="errors.other_zipcode" />
                        <InputText label="Miejscowość" v-model="form.other_city" :required="true" :error="errors.other_city" />
                    </div>
                </template>

                <hr>

                <h3>Informacje o taryfie</h3>

                <FormInfo type="2" title="Taryfa" :value="options.tariff" :tip="true" @tip="onTip('tariff')" />
                <FormInfo class="StepSmeFormInvoice__tariff" type="2" title="Deklarowane zużycie roczne" :value="usageformat" :tip="true" @tip="onTip('usage')" />

                <LabeledInput type="list" label="Rezerwowy sprzedawca energii" hint="Ustawowy" :items="options.si_vendors" v-model="form.spare" :error="errors.spare" edit_type="icon" :tip="true" @tip="onTip('spare')" />
                <LabeledInput type="date" label="Od kiedy chcesz korzystać z energii od Respect Energy?" v-model="form.startdate" :date_min="startdatemin" :date_onlyfirsts="true" :error="errors.startdate" edit_type="icon" :tip="true" @tip="onTip('startdate')" />

                <p class="StepSmeFormInvoice__legend">Pamiętaj, aby sprawdzić okres wypowiedzenia obowiązujący u Twojego obecnego sprzedawcy. Mogą się z tym wiązać kary umowne.</p>
            </div>
        </StepBox>
    `,

    emits: [
        'next',
        'back',
        'goto',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        step: { type: Number, required: true },
    },

    setup(props, ctx) {
        const sent = ref(false);

        const form = ref({
            spare: props.options.spare_vendor,
            startdate: props.options.startdate ? new Date(props.options.startdate) : null,

            ppe_active: props.options.ppe_active,
            ppe_street: props.options.ppe_street,
            ppe_buildingno: props.options.ppe_buildingno,
            ppe_apartmentno: props.options.ppe_apartmentno,
            ppe_zipcode: props.options.ppe_zipcode,
            ppe_city: props.options.ppe_city,

            other_active: props.options.other_active,
            other_street: props.options.other_street,
            other_buildingno: props.options.other_buildingno,
            other_apartmentno: props.options.other_apartmentno,
            other_zipcode: props.options.other_zipcode,
            other_city: props.options.other_city,
        });

        const errors = ref({
            spare: computed(() => inputError('spare', sent.value)),

            ppe_street: computed(() => inputError('ppe_street', sent.value)),
            ppe_buildingno: computed(() => inputError('ppe_buildingno', sent.value)),
            ppe_apartmentno: computed(() => inputError('ppe_apartmentno', sent.value)),
            ppe_zipcode: computed(() => inputError('ppe_zipcode', sent.value)),
            ppe_city: computed(() => inputError('ppe_city', sent.value)),

            other_street: computed(() => inputError('other_street', sent.value)),
            other_buildingno: computed(() => inputError('other_buildingno', sent.value)),
            other_apartmentno: computed(() => inputError('other_apartmentno', sent.value)),
            other_zipcode: computed(() => inputError('other_zipcode', sent.value)),
            other_city: computed(() => inputError('other_city', sent.value)),
        });

        const startdatemin = computed(() => {
            const pad = moment().date() > 25 ? 2 : 1;
            let r = moment().set({ date: 1 }).add({ month: pad + 1 });

            return r.toDate();
        });

        const usageformat = computed(() => {
            return Utils.usageFormat(props.options.usage) + ' kWh';
        });

        const osd = computed(() => {
            return props.options.si_osds?.find(v => v.id == props.options.osd);
        });

        const prev_vendor = computed(() => {
            return props.options.si_vendors?.find(v => v.id == props.options.prev_vendor);
        });

        const spare_vendor = computed(() => {
            return props.options.si_vendors?.find(v => v.id == props.options.spare_vendor);
        });

        function assertError(field: string, force: boolean = false) {
            // field == 'spare' && (force || form.value.spare) && Validator.assertRequired(form.value.spare);

            if (form.value.ppe_active) {
                field == 'ppe_street' && (force || form.value.ppe_street) && Validator.assertRequired(form.value.ppe_street);
                field == 'ppe_buildingno' && (force || form.value.ppe_buildingno) && Validator.assertRequired(form.value.ppe_buildingno);
                field == 'ppe_zipcode' && (force || form.value.ppe_zipcode) && Validator.assertZipCode(form.value.ppe_zipcode);
                field == 'ppe_city' && (force || form.value.ppe_city) && Validator.assertRequired(form.value.ppe_city);
            }

            if (form.value.other_active) {
                field == 'other_street' && (force || form.value.other_street) && Validator.assertRequired(form.value.other_street);
                field == 'other_buildingno' && (force || form.value.other_buildingno) && Validator.assertRequired(form.value.other_buildingno);
                field == 'other_zipcode' && (force || form.value.other_zipcode) && Validator.assertZipCode(form.value.other_zipcode);
                field == 'other_city' && (force || form.value.other_city) && Validator.assertRequired(form.value.other_city);
            }
        }

        function assertValid() {
            assertError('spare', true);

            assertError('ppe_street', true);
            assertError('ppe_buildingno', true);
            assertError('ppe_apartmentno', true);
            assertError('ppe_zipcode', true);
            assertError('ppe_city', true);

            assertError('other_street', true);
            assertError('other_buildingno', true);
            assertError('other_apartmentno', true);
            assertError('other_zipcode', true);
            assertError('other_city', true);
        }


        function inputError(field: string, force: boolean = false) {
            try {
                assertError(field, force);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        async function onNext() {
            try {
                sent.value = true;

                assertValid();

                const r = await Utils.setCart('form.invoice', {
                    form: form.value,
                }, false);

                Tracking.doEvent('b2bsme_companydata_step2');

                ctx.emit('next');
            } catch (e) {
                nextTick(() => {
                    const $first = $('.InputWidget--error, .InputCheckbox--error').first();
                    Utils.scrollToEl($first);
                });
            }
        }

        return {
            sent,
            form,
            errors,

            startdatemin,
            usageformat,
            osd,
            prev_vendor,
            spare_vendor,

            onNext,
        };
    },
});
